import { useEffect, useRef, useState } from 'react';
import { extractApiErrorMessage } from '@features/billing-readiness/utils/errorUtils';
import { useEventTracking } from '@services/statsig';

import { useCorrectionPreparationQuery, useCreateCorrectionMutation } from '../../store/api';
import type { CreateCorrectionRequest } from '../../types';

import { CorrectionPreparationDialogView } from './CorrectionPreparationDialogView';

/** Mount on demand with a claim-id key so each visit starts with fresh evidence. */
export function CorrectionPreparationDialog({
  claimId,
  onClose,
  onOpened,
}: {
  claimId: string;
  onClose: () => void;
  onOpened: (claimId: string) => void;
}) {
  const {
    currentData: preview,
    isFetching,
    isError,
    refetch,
  } = useCorrectionPreparationQuery({ claimId }, { refetchOnMountOrArgChange: true });
  const [create, { isLoading: creating }] = useCreateCorrectionMutation();
  const { billing } = useEventTracking();
  const [choice, setChoice] = useState('');
  const [reference, setReference] = useState('');
  const [source, setSource] = useState<CreateCorrectionRequest['reference_source']>();
  const [note, setNote] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);
  const trackedOpen = useRef(false);
  useEffect(() => {
    if (!trackedOpen.current) {
      billing.correctionPreparationChanged({ claim_id: claimId, action: 'opened' });
      trackedOpen.current = true;
    }
  }, [billing, claimId]);

  const candidate = preview?.candidates?.find(item => item.remittance_claim_id === choice);
  // A retry can reveal different evidence under the same remittance ID.
  // Keep the biller's documentation, but require a fresh confirmation.
  const evidence = JSON.stringify({
    payer: preview?.payer_uuid,
    status: preview?.status,
    stored: choice === 'stored' ? preview?.stored_reference : undefined,
    candidate,
    existingReference: preview?.resolved_reference,
    referenceReviewRequired: preview?.reference_review_required,
  });
  useEffect(() => {
    setConfirmed(false);
  }, [evidence]);
  const existing = preview?.status === 'existing_draft';
  const needsReview = existing
    ? !!preview?.reference_review_required
    : choice === 'manual' ||
      preview?.status !== 'ready' ||
      candidate?.eligibility === 'review_required';
  const selectionValid =
    choice === 'manual'
      ? !!reference.trim() && !!source
      : choice === 'stored'
        ? !!preview?.stored_reference
        : (!!candidate && candidate.eligibility !== 'not_applicable') ||
          (!choice && preview?.status === 'ready');
  const sourceRequired = needsReview && (existing || choice === 'manual');
  const canConfirm =
    !!preview &&
    preview.status !== 'unavailable' &&
    (existing || selectionValid) &&
    (!sourceRequired || !!source) &&
    (!needsReview || (confirmed && !!note.trim()));

  const handleConfirm = async () => {
    if (!canConfirm || isFetching || inFlight.current) return;
    inFlight.current = true;
    setError(null);
    billing.correctionPreparationChanged({ claim_id: claimId, action: 'confirmed' });
    const selection: CreateCorrectionRequest = existing
      ? needsReview
        ? { expected_reference_number: preview?.resolved_reference, reference_source: source }
        : {}
      : choice === 'manual'
        ? { original_reference_number: reference.trim(), reference_source: source }
        : choice === 'stored'
          ? { reference_source: 'stored', expected_reference_number: preview?.stored_reference }
          : candidate
            ? {
                remittance_claim_id: candidate.remittance_claim_id,
                expected_reference_number: candidate.reference,
              }
            : {};
    if (needsReview)
      Object.assign(selection, { payer_reference_confirmed: confirmed, review_note: note.trim() });
    try {
      const child = await create({ claimId, createCorrectionRequest: selection }).unwrap();
      if (!child.id) throw new Error('Missing correction ID');
      onOpened(child.id);
      onClose();
    } catch (cause) {
      setError(extractApiErrorMessage(cause, 'Could not prepare the correction. Please retry.'));
      void refetch();
    } finally {
      inFlight.current = false;
    }
  };

  return (
    <CorrectionPreparationDialogView
      preview={preview}
      loading={isFetching}
      loadFailed={isError}
      creating={creating}
      error={error}
      choice={choice}
      reference={reference}
      source={source}
      note={note}
      confirmed={confirmed}
      canConfirm={canConfirm}
      needsReview={needsReview}
      onChoice={value => {
        setChoice(value);
        setSource(undefined);
        setConfirmed(false);
        setNote('');
        setError(null);
        billing.correctionPreparationChanged({ claim_id: claimId, action: 'reference_selected' });
      }}
      onReference={value => {
        setReference(value);
        setConfirmed(false);
      }}
      onSource={value => {
        setSource(value);
        setConfirmed(false);
      }}
      onNote={setNote}
      onConfirmed={setConfirmed}
      onClose={() => {
        if (!inFlight.current) {
          billing.correctionPreparationChanged({ claim_id: claimId, action: 'canceled' });
          onClose();
        }
      }}
      onRetry={() => {
        billing.correctionPreparationChanged({ claim_id: claimId, action: 'retry_requested' });
        void refetch();
      }}
      onConfirm={() => {
        void handleConfirm();
      }}
    />
  );
}
